export { formatNumber, squareMetersToHectares } from "./number-format";
export {
  isAgricultural,
  isCountryEstate,
  isCommercial,
  isHomeSale,
  isInvestmentProperty,
  isOwnProperty,
  isLand,
  isVillaRaekkehusOrFritidshus,
} from "./case-classification";
import { CaseData, CMSSection, Openhouse } from "@/cms/models";
import { getCaseClassification, isRental } from "./case-label";
import { systemFontFamily } from "./theme-fonts";
import {
  faFacebookF,
  faInstagram,
  faLinkedinIn,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faQuestion } from "@fortawesome/sharp-light-svg-icons";
import * as ics from "ics";
import { isToday, isTomorrow } from "date-fns";
import {
  Lato,
  Merriweather,
  Montserrat,
  Roboto,
  Roboto_Slab,
  Titillium_Web,
  Cormorant_Garamond,
  Plus_Jakarta_Sans,
  Quicksand,
  Arya,
  Prata,
  Bodoni_Moda,
  Inter,
  Playfair_Display,
  DM_Sans,
  Fraunces,
  Jost,
  Work_Sans,
} from "@/fonts/google";

const interFont = Inter({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const cormorantGaramond = Cormorant_Garamond({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const plusJakartaSans = Plus_Jakarta_Sans({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const classicFont = Roboto_Slab({
  weight: ["300", "400", "600", "700"],
  subsets: ["latin"],
  display: "swap",
});

const modernFont = Roboto({
  weight: ["300", "700"],
  subsets: ["latin"],
  display: "swap",
});

const latoFont = Lato({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const titilliumWeb = Titillium_Web({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const montserrat = Montserrat({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const merriweather = Merriweather({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const standardFont = Roboto({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const quicksand = Quicksand({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const aryaFont = Arya({
  weight: ["400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const prataFont = Prata({
  weight: ["400"],
  subsets: ["latin"],
  display: "swap",
});

const bodoniModa = Bodoni_Moda({
  weight: "variable",
  axes: ["opsz"],
  subsets: ["latin", "latin-ext"],
  display: "swap",
});

const playfairDisplay = Playfair_Display({
  weight: ["400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const dmSans = DM_Sans({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const fraunces = Fraunces({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const jost = Jost({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

const workSans = Work_Sans({
  weight: ["300", "400", "700"],
  subsets: ["latin"],
  display: "swap",
});

function getOpenHouseTextDays(openHouse: Openhouse): string {
  if (isToday(openHouse.startDate!)) {
    return "I dag";
  } else if (isTomorrow(openHouse.startDate!)) {
    return "I morgen";
  }
  return formatDate(openHouse!.startDate!, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

export function transformImageUrl(url?: string | undefined | null) {
  if (url && url?.length > 0) {
    const mediaServer = process.env.MEDIA_SERVER_URL ?? "";
    if (mediaServer.length > 0) {
      const replacedUrl = url.replace(mediaServer, "/media/");
      return replacedUrl;
    }
    return url;
  }
  return url;
}

export function GetFont(fontType: string) {
  switch (fontType) {
    case "classic":
      return classicFont;
    case "modern":
      return modernFont;
    case "inter":
      return interFont;
    case "lato":
      return latoFont;
    case "titillium_web":
      return titilliumWeb;
    case "montserrat":
      return montserrat;
    case "merriweather":
      return merriweather;
    case "cormorantGaramond":
      return cormorantGaramond;
    case "plusJakartaSans":
      return plusJakartaSans;
    case "roboto-montserrat":
      return montserrat;
    case "montserrat-only":
      return montserrat;
    case "quicksand":
      return quicksand;
    case "arya":
      return aryaFont;
    case "prata":
      return prataFont;
    case "bodoniModa":
      return bodoniModa;
    case "bodoniModa-system-font":
      return standardFont;
    case "linotypeDidot-system-font":
      return { className: "", style: { fontFamily: systemFontFamily } };
    case "playfairDisplay":
      return playfairDisplay;
    case "dmSans":
      return dmSans;
    case "fraunces":
      return fraunces;
    case "jost":
      return jost;
    case "workSans":
      return workSans;
    case "playfairDisplay-dmSans":
      return dmSans;
    case "fraunces-jost":
      return jost;
    case "cormorantGaramond-workSans":
      return workSans;
    default:
      return standardFont;
  }
}

export function getOpenHouseText(openHouse: Openhouse): string {
  return [
    capitalize(getOpenHouseTextDays(openHouse)),
    formatTime(openHouse.startDate!),
    "-",
    formatTime(openHouse!.endDate!),
  ].join(" ");
}

export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions,
) {
  options = options ?? {};
  if (date instanceof Date) {
    return date.toLocaleDateString("da-DK", options);
  }
  return new Date(date).toLocaleDateString("da-DK", options);
}

export function fullScreen(ref: React.RefObject<any>, isFullscreen: boolean) {
  if (!isFullscreen) {
    if (ref.current) {
      if (ref.current.requestFullscreen) {
        ref.current.requestFullscreen();
      } else if (ref.current.webkitRequestFullscreen) {
        ref.current.webkitRequestFullscreen();
      } else if (ref.current.mozRequestFullScreen) {
        ref.current.mozRequestFullScreen();
      } else if (ref.current.msRequestFullscreen) {
        ref.current.msRequestFullscreen();
      }
    }
  } else {
    document.exitFullscreen();
  }
}
export function formatTime(date: string | Date) {
  if (date instanceof Date) {
    return date.toLocaleTimeString("da-DK", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return new Date(date).toLocaleTimeString("da-DK", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function createIcsEvent(
  title: string,
  startDate: Date,
  endDate: Date,
  location?: string,
): string {
  return ics.createEvent({
    title: title,
    location: location,
    start: [
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startDate.getHours(),
      startDate.getMinutes(),
    ],
    end: [
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      endDate.getHours(),
      endDate.getMinutes(),
    ],
  })!.value!;
}

export function mapSocialIcon(namespace: string) {
  switch (namespace) {
    case "some.facebook":
      return faFacebookF;
    case "some.linkedin":
      return faLinkedinIn;
    case "some.instagram":
      return faInstagram;
    case "some.x":
      return faXTwitter;
    case "some.email":
      return faEnvelope;
    default:
      return faQuestion;
  }
}

export function formatToIcs(date: string | Date | undefined) {
  if (!date) return "";
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

export function capitalize(s: string) {
  return s && String(s[0]).toUpperCase() + String(s).slice(1);
}

export function formatMonthlyRental(number?: number | string) {
  if (typeof number === "string") number = parseFloat(number);

  const rentAmount = new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    notation: "standard",
    maximumFractionDigits: 0,
  }).format(number ?? 0);

  return `${rentAmount} / md.`;
}

export function formatYearlyRental(number?: number | string) {
  if (typeof number === "string") number = parseFloat(number);

  const rentAmount = new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    notation: "standard",
    maximumFractionDigits: 0,
  }).format(number ?? 0);

  return `${rentAmount} / år`;
}

export function formatPrice(number?: number | string) {
  if (typeof number === "string") number = parseFloat(number);

  return new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    notation: "standard",
    maximumFractionDigits: 0,
  }).format(number ?? 0);
}

export function isHouse(list: string[]) {
  const houseClassifications = [
    "dk.realequity.estate.villa",
    "dk.realequity.estate.raekkehus",
    "dk.realequity.estate.fritidshus",
  ];
  return list.some((x) => houseClassifications.includes(x));
}

export function isApartment(list: string[]) {
  const apartmentClassifications = [
    "dk.realequity.taxonomy.ejerform.ejerlejlighed",
  ];
  return list.some((x) => apartmentClassifications.includes(x));
}

export function hasAgriculturalTypes(list: string[]) {
  const agriculturalTypes = [
    "dk.realequity.estate.planteavl",
    "dk.realequity.estate.svineproduktion",
    "dk.realequity.estate.skovlandbrugsejendom",
    "dk.realequity.estate.kvaegbrug",
  ];
  return list.some((x) => agriculturalTypes.includes(x));
}

export function isFarm(list: string[]) {
  const farmClassifications = [
    "dk.realequity.estate.landbrugsejendom",
    "dk.realequity.estate.lystejendom",
  ];
  return list.some((x) => farmClassifications.includes(x));
}

export function isAndels(list: string[]) {
  const andelsClassifications = [
    "dk.realequity.taxonomy.ejerform.andel",
    "dk.realequity.taxonomy.opdrag.salg",
  ];
  return andelsClassifications.every((x) => list.includes(x));
}

export function isLejeBolig(list: string[]) {
  const houseClassifications = [
    "dk.realequity.taxonomy.kategori.helaarsbolig",
    "dk.realequity.taxonomy.kategori.fritidsbolig",
  ];
  return isRental(list) && list.some((x) => houseClassifications.includes(x));
}

export function isOmfattetAfLofe(list: string[]) {
  const lofeClassifications = [
    "dk.realequity.taxonomy.markeringerpaasag.omfattetaflofe",
  ];
  return list.some((x) => lofeClassifications.includes(x));
}

export function shouldShowOwnerExpenses(property: CaseData) {
  if (property.ownerExpensesTotalMonthly <= 0) {
    return false;
  }

  const caseClassification = getCaseClassification(property);

  return (
    !(property.isAgricultural || property.isCommercial) ||
    isOmfattetAfLofe(caseClassification)
  );
}

export function hasParking(list: string[]) {
  const parkingClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.parkering",
  ];
  return list.some((x) => parkingClassifications.includes(x));
}

export function petsAllowed(list: string[]) {
  return list.some((x) =>
    ["dk.realequity.taxonomy.markeringerpaaejendom.husdyrtilladt"].includes(x),
  );
}

export function sublettingAllowed(list: string[]) {
  return list.some((x) =>
    ["dk.realequity.taxonomy.markeringerpaaejendom.fremlejetilladt"].includes(
      x,
    ),
  );
}

export function hasElevator(list: string[]) {
  const parkingClassifications = [
    "dk.realequity.taxonomy.markeringerpaaejendom.elevator",
  ];
  return list.some((x) => parkingClassifications.includes(x));
}

export function getBalconyTaxon(caseData: CaseData) {
  return caseData.estateTaxons.find(
    (x) =>
      x.taxonNamespace === "dk.realequity.taxonomy.markeringerpaaejendom.altan",
  );
}

export function getEstateFacilities(property: CaseData) {
  return property.estateTaxons.filter((x) =>
    x.taxonNamespace.startsWith("dk.realequity.taxonomy.markeringerpaaejendom"),
  );
}

/** With explicit spacing (`cmsSection.explicitSpacing`) the type's padding and margin are not defaults: absent is 0. */
export function getDefaultCmsSection(
  cmsSection: CMSSection<any>,
  defaultUI?: any,
): CMSSection<any> {
  if (cmsSection?.explicitSpacing) {
    const typeUI = Object.fromEntries(
      Object.entries(defaultUI ?? {}).filter(
        ([key]) => key !== "padding" && key !== "margin",
      ),
    );
    return {
      ...cmsSection,
      ui: {
        ...{ width: "2xl", marginTop: "none", marginBottom: "none" },
        ...typeUI,
        ...cmsSection.ui,
      },
    };
  }
  return {
    ...cmsSection,
    ui: {
      ...{
        width: "2xl",
        padding: "md | lg",
        marginTop: "none",
        marginBottom: "none",
      },
      ...defaultUI,
      ...cmsSection?.ui,
    },
  };
}

export function getPriceInfo(property: CaseData): string {
  const list = getCaseClassification(property);

  if (isRental(list) && property.isCommercial) {
    return property.isActive && property.rentAnnually > 0
      ? formatYearlyRental(property.rentAnnually)
      : "-";
  }

  if (isRental(list)) {
    return property.isActive && property.rentMonthly > 0
      ? formatMonthlyRental(property.rentMonthly)
      : "-";
  }

  return property.isActive && property.price > 0
    ? formatPrice(property.price)
    : "-";
}

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// getCaseUrl + Danish transliteration live in ./case-url (kept dependency-free
// so they are unit-testable). Re-exported here so existing imports such as
// `import { getCaseUrl } from "@/utils/helper"` continue to work unchanged.
export {
  getCaseUrl,
  transliterateDanish,
  canonicalCaseRedirect,
} from "./case-url";

export {
  getCaseClassification,
  isRental,
  getLabel,
  getCaseDetailSublabel,
} from "./case-label";
export type { CaseClassificationInput, CaseLabelInput } from "./case-label";

export function formatPhoneNumberObject(data: {
  prefix?: string;
  number?: string;
}) {
  return formatPhoneNumber(data?.prefix ?? "", data?.number ?? "");
}

export function formatPhoneNumber(
  prefix: string,
  phone: string | undefined,
): string {
  if (!phone) return "";

  const digits = phone.replace(/\s+/g, "");

  if (prefix === "+45") {
    const digitGroups = digits.match(/.{1,2}/g) || [];
    const formattedDigits = digitGroups.join(" ");
    return prefix ? `${prefix} ${formattedDigits}` : formattedDigits;
  }

  return prefix ? `${prefix} ${digits}` : digits;
}

export function getPrimaryOrFirst<T extends { primary: boolean }>(
  list: T[],
): T | undefined {
  return list.find((item) => item.primary) || list[0];
}

export const DefaultTake = 99;

export function getSearchParamCaseInsentive(
  searchParams: URLSearchParams,
  key: string,
): string | null {
  const lowerKey = key.toLowerCase();
  for (const [paramKey, value] of searchParams.entries()) {
    if (paramKey.toLowerCase() === lowerKey) {
      return value;
    }
  }
  return null;
}

export function getEffectiveDomain(
  host: string,
  primaryDomain?: string,
): string {
  if (!primaryDomain) {
    return `https://${host}`;
  }

  if (primaryDomain === "www.") {
    if (host.startsWith("www.")) {
      return `https://${host}`;
    } else {
      const nakedDomain = host.replace("www.", "");
      return `https://${primaryDomain}${nakedDomain}`;
    }
  }

  if (primaryDomain === ".") {
    if (host.startsWith("www.")) {
      const nakedDomain = host.replace("www.", "");
      return `https://${nakedDomain}`;
    } else {
      return `https://${host}`;
    }
  }

  return `https://${host}`;
}

export function getPrimaryDomainRedirect(
  host: string,
  primaryDomain?: string,
): string | null {
  if (!primaryDomain) {
    return null;
  }

  if (primaryDomain === "www.") {
    if (host.startsWith("www.")) {
      // Do nothing, already on www
      return null;
    } else {
      const nakedDomain = host.replace("www.", "");
      return `https://${primaryDomain}${nakedDomain}`;
    }
  }

  if (primaryDomain === ".") {
    if (host.startsWith("www.")) {
      const nakedDomain = host.replace("www.", "");
      return `https://${nakedDomain}`;
    } else {
      // Do nothing, already on . (naked domain)
      return null;
    }
  }

  return null;
}

export const getParamsHash = async (value: unknown): Promise<string> => {
  const normalized = normalizeForHash(value);
  const json = JSON.stringify(normalized) || "{}";

  const encoder = new TextEncoder();
  const data = encoder.encode(json);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  return [...new Uint8Array(hashBuffer)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

function normalizeForHash(value: unknown): unknown {
  if (value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(normalizeForHash);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  const obj = value as Record<string, unknown>;
  const sortedKeys = Object.keys(obj).sort();

  const result: Record<string, unknown> = {};
  for (const key of sortedKeys) {
    result[key] = normalizeForHash(obj[key]);
  }

  return result;
}
